/**
 * Les formes rendues par l'API BDESE, telles qu'elle les rend vraiment.
 *
 * Relevées dans `bdese.controller.ts` (`resumer()`) et `bdese-acces.service.ts`
 * (`rendrePourDestinataire()`), pas devinées. Un type qui ment sur la réponse
 * fait tomber l'écran au premier affichage — c'est arrivé le 25/08/2026 sur les
 * modules du dossier.
 */

/** L'état d'un exercice, tel que la base l'autorise. */
export type StatutRapport =
  | 'brouillon'
  | 'en_cours'
  | 'complet'
  | 'publie'
  | 'archive';

/**
 * La catégorie de seuil est DÉDUITE de l'effectif L1111-2, jamais saisie.
 * C'est elle qui décide du référentiel applicable — R2312-8 ou R2312-9.
 */
export type CategorieSeuil = 'moins_de_50' | 'de_50_a_299' | 'de_300_et_plus';

export interface RapportResume {
  uuid: string;
  folderUuid: string;
  annee: number;
  statut: StatutRapport;
  categorieSeuil: CategorieSeuil;
  referentielCode: string;
  referentielVersion: string;
  justificationSeuil: string | null;
}

/**
 * D'où vient un chiffre. C'est l'information qui rend un document défendable :
 * un élu qui conteste demande d'où sort la valeur.
 */
export type SourceValeur =
  | 'paie'
  | 'calcul_automatique'
  | 'entreprise'
  | 'indisponible'
  | 'confidentiel';

/** Chiffrée, tendance, ou indisponible avec motif — R2312-10. */
export type RegimeValeur = string;

export interface PointDeDonnees {
  indicateurCode: string;
  cheminArticle: string;
  anneePeriode: number;
  typePeriode: string;
  regimeValeur: RegimeValeur;
  motifIndisponible: string | null;
  source: SourceValeur;
  /** Forme réellement rendue par `BdeseAccesService`. */
  valeurNumerique?: number | null;
  valeurTexte?: string | null;
  valeurJson?: Record<string, unknown> | null;
  alertes?: string[];
  /** Présent notamment quand une valeur confidentielle est masquée. */
  valeur?: unknown;
  /** Vrai quand la valeur est retirée au destinataire. */
  confidentiel?: boolean;
  confidentielJusquAu?: string;
  /** Ce qui remplace la valeur : la mention et ses deux fondements. */
  mention?: string;
  /** Signalée quand un marquage est irrégulier (R2312-13). */
  anomalie?: string;
}

export interface RapportConsulte {
  rapport: RapportResume;
  /** L'article qui fonde l'accès du destinataire — il s'affiche, il ne se tait pas. */
  fondementAcces: string | Record<string, unknown>;
  pointsDeDonnees: PointDeDonnees[];
}

export const LIBELLE_CATEGORIE: Record<CategorieSeuil, string> = {
  moins_de_50: 'moins de 50 salariés',
  de_50_a_299: 'de 50 à 299 salariés',
  de_300_et_plus: '300 salariés et plus',
};

export const LIBELLE_STATUT: Record<StatutRapport, string> = {
  brouillon: 'Brouillon',
  en_cours: 'En cours',
  complet: 'Complet',
  publie: 'Publié',
  archive: 'Archivé',
};

/** Ce que dit une source, en mots d'utilisateur. */
export const LIBELLE_SOURCE: Record<string, string> = {
  paie: 'paie',
  entreprise: 'entreprise',
  indisponible: 'indisponible',
  confidentiel: 'confidentiel',
};

/** Un indicateur attendu par la loi, tel que le serveur le décrit. */
export interface DefinitionIndicateur {
  code: string;
  libelle: string;
  cheminArticle: string;
  typeDonnee: string;
  unite?: string;
  /** `automatique` = calculé depuis la paie ; sinon, à renseigner. */
  alimentation: string;
  socleMinimal: boolean;
  /** « Le cas échéant » du texte : son absence n'est PAS une lacune. */
  conditionnel?: boolean;
  conditionApplicabilite?: string;
}

export interface RubriqueReferentiel {
  code: string;
  libelle: string;
  cheminArticle: string;
  indicateurs: DefinitionIndicateur[];
}

/**
 * Ce que la loi attend sur un exercice — DÉDUIT par le serveur de la catégorie
 * de seuil. Le navigateur n'en porte aucune copie : elle divergerait au premier
 * décret, et personne ne le verrait avant un contrôle.
 */
export interface Referentiel {
  categorieSeuil: CategorieSeuil;
  rubriques: RubriqueReferentiel[];
}

/** Ce qu'on envoie pour renseigner une valeur. */
export interface SaisieIndicateur {
  indicateurCode: string;
  anneePeriode: number;
  typePeriode: 'realise' | 'projection';
  regimeValeur: 'chiffree' | 'tendance' | 'indisponible';
  valeurNumerique?: number;
  valeurTexte?: string;
  valeurJson?: unknown;
  motifIndisponible?: string;
  estConfidentiel?: boolean;
  confidentielJusquAu?: string;
}

export type SupportBdese = 'informatique' | 'papier';
export type CanalInformationBdese =
  | 'portail'
  | 'courriel'
  | 'portail_et_courriel';

export interface ModalitesBdese {
  uuid: string;
  folderUuid: string;
  support: SupportBdese;
  modalitesAcces: string | null;
  modalitesInformation: string | null;
  canalInformation: CanalInformationBdese | null;
  effetUtileAtteste: boolean;
  fixeesLe: string | null;
  fixeesPar: string | null;
}

export interface ManquementBdese {
  gravite: 'bloquant' | 'reserve';
  fondement: string;
  constat: string;
  correction: string;
}

export interface ConformiteModalitesBdese {
  sousAccord: boolean;
  effectif: number | null;
  supportImpose: SupportBdese | null;
  manquements: ManquementBdese[];
  conforme: boolean;
}

export interface EtatModalitesBdese {
  modalites: ModalitesBdese | null;
  conformite: ConformiteModalitesBdese;
  /** Décidé par l'API à partir de la même matrice que le PUT. */
  modifiable: boolean;
}

export interface FixerModalitesBdese {
  support: SupportBdese;
  modalitesAcces: string;
  modalitesInformation: string;
  canalInformation: CanalInformationBdese;
  effetUtileAtteste: boolean;
}

export type StatutInformationBdese =
  | 'en_attente'
  | 'en_cours'
  | 'envoye'
  | 'echec'
  | 'annule';

export interface InformationBdese {
  uuid: string;
  statut: StatutInformationBdese;
  tentatives: number;
  prochaineTentativeLe?: string | null;
  envoyeLe?: string | null;
}

export interface EtatInformationsBdese {
  informations: InformationBdese[];
  /** Décidé par l'API ; son absence interdit l'action. */
  repriseAutorisee: boolean;
}
